<template>
  <div class="exponential-pro-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$y = a^x$</div>
    </div>

    <div class="split-layout">
      <!-- 曲线追踪区域 -->
      <div class="simulation-stage dark-stage flex-1">
        <svg width="100%" height="340" viewBox="-200 -300 400 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="exp-glow-p" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
           </defs>

           <!-- Grid -->
           <g stroke="rgba(241, 245, 249, 0.05)" stroke-width="1.5">
              <line v-for="i in 9" :key="'v'+i" :x1="-200 + (i-1)*50" y1="-300" :x2="-200 + (i-1)*50" y2="300" />
              <line v-for="i in 13" :key="'h'+i" x1="-200" :y1="-300 + (i-1)*50" x2="200" :y2="-300 + (i-1)*50" />
           </g>
           
           <!-- Axes -->
           <line x1="-200" y1="0" x2="200" y2="0" stroke="#475569" stroke-width="2.5" />
           <line x1="0" y1="-300" x2="0" y2="300" stroke="#475569" stroke-width="2.5" />
           <text x="180" y="20" fill="#94a3b8" font-size="16" font-weight="900">X轴</text>
           <text x="10" y="-280" fill="#94a3b8" font-size="16" font-weight="900">Y轴</text>

           <!-- Math Curve -->
           <path :d="curvePath" fill="none" stroke="#22d3ee" stroke-width="5" filter="url(#exp-glow-p)" stroke-linecap="round" />
           
           <!-- Fixed Point (0, 1) -->
           <circle cx="0" cy="-30" r="7" fill="#fbbf24" stroke="#fff" stroke-width="2"/>
           <text x="10" y="-35" fill="#fbbf24" font-size="14" font-weight="900">(0, 1)</text>

           <!-- Dynamic Probe -->
           <g :transform="`translate(${currentX * 30}, ${-currentY * 30})`">
              <circle r="8" fill="#f43f5e" stroke="#fff" stroke-width="2.5" />
              <line x1="0" y1="0" :x2="0" :y2="currentY * 30" stroke="#94a3b8" stroke-width="1.5" stroke-dasharray="6,4" opacity="0.6" />
              <line x1="0" y1="0" :x2="-currentX * 30" y2="0" stroke="#94a3b8" stroke-width="1.5" stroke-dasharray="6,4" opacity="0.6" />
           </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>函数值 y</label>
              <span class="val">{{ currentY.toFixed(2) }}</span>
           </div>
        </div>
      </div>

      <!-- 病毒/细胞增殖模拟 -->
      <div class="simulation-stage logic-panel">
        <div class="logic-header">实体增殖模拟 (底数 {{ base.toFixed(1) }})</div>
        <svg width="100%" height="340" viewBox="0 0 300 400" preserveAspectRatio="xMidYMid meet">
            <g v-for="(level, yIdx) in discreteDots" :key="'L'+yIdx">
               <!-- 绘制分支连线 -->
               <g v-if="yIdx > 0">
                 <g v-for="(parent, pIdx) in discreteDots[yIdx-1]" :key="'p'+yIdx+'-'+pIdx">
                    <line v-for="(child, cIdx) in level" :key="'c'+yIdx+'-'+cIdx" v-if="cIdx >= pIdx*Math.floor(base) && cIdx < (pIdx+1)*Math.floor(base)"
                          :x1="parent.x" :y1="parent.y" :x2="child.x" :y2="child.y" 
                          stroke="rgba(34, 211, 238, 0.3)" stroke-width="1.5" />
                 </g>
               </g>
               <!-- 绘制细胞体 -->
               <circle v-for="(dot, dIdx) in level" :key="'d'+yIdx+'-'+dIdx"
                       :cx="dot.x" :cy="dot.y" :r="dot.r" :fill="yIdx === Math.floor(currentX) ? '#f43f5e' : '#22d3ee'" />
            </g>
            
            <text x="150" y="380" fill="rgba(255,255,255,0.5)" font-size="14" font-weight="900" text-anchor="middle">代数/周期 X (仅整数演示): {{ Math.floor(currentX) }}</text>
        </svg>
      </div>
    </div>

    <!-- 控制面板 -->
    <div class="compact-controls">
      <div class="control-card">
        <div class="control-grid-mini">
           <div class="s-item">
              <label>底数 a ({{ base > 1 ? '爆炸性增长' : '指数衰减' }})</label>
              <el-slider v-model="base" :min="0.5" :max="4.0" :step="0.5" size="small"></el-slider>
           </div>
           <div class="s-item">
              <label>时间探针 x ({{ currentX.toFixed(1) }})</label>
              <el-slider v-model="currentX" :min="0" :max="5" :step="0.1" size="small"></el-slider>
           </div>
        </div>
      </div>

      <div class="discovery-card-compact" v-latex>
         <i class="el-icon-s-opportunity" style="color:#0ea5e9;"></i>
         <div class="discovery-content">
            <p><strong>指数的本质：</strong>每一次的增量完全依赖于当前的存量，引发无法阻挡的“滚雪球”效应。即便是微小的大于1的底数，在足够的周期 x 后都会产生数量学的奇迹。在生物种群、核裂变和复利中极为常见。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExponentialFunctionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      base: 2.0,
      currentX: 3.0
    }
  },
  computed: {
    currentY() {
      return Math.pow(this.base, this.currentX);
    },
    curvePath() {
      let pathArr = [];
      // 1 unit = 30px
      for (let x = -6; x <= 6; x += 0.1) {
        let y = Math.pow(this.base, x);
        if (y > 15) continue;
        pathArr.push(`${(x * 30).toFixed(1)},${(-y * 30).toFixed(1)}`);
      }
      return `M ${pathArr.join(' L ')}`;
    },
    discreteDots() {
      // Create tiers of dots simulating cell division up to currentX (integer bounded)
      let levels = [];
      let activeX = Math.floor(this.currentX);
      if (activeX < 0) activeX = 0;
      if (activeX > 6) activeX = 6;
      let b = Math.floor(this.base);
      if (b < 1) b = 1;
      
      let totalDotsLimit = 500;
      let currentDraw = 0;

      for (let i = 0; i <= activeX; i++) {
         let count = Math.pow(b, i);
         if (currentDraw + count > totalDotsLimit) break;
         let spacing = 300 / (count + 1);
         let yPos = 30 + i * 60;
         let row = [];
         let radius = count > 20 ? 3 : (count > 5 ? 5 : 8);
         
         for (let j = 0; j < count; j++) {
            row.push({
               x: spacing * (j + 1),
               y: yPos,
               r: radius
            });
         }
         levels.push(row);
         currentDraw += count;
      }
      return levels;
    }
  }
}
</script>

<style scoped lang="scss">
.exponential-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 14px; color: #1e293b; font-weight: 700; } .formula-chip { background: #f0fdfa; color: #0d9488; border: 1px solid #99f6e4; padding: 6px 16px; border-radius: 20px; font-weight: 900; font-family: monospace; font-size: 15px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 340px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 1; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; }
.logic-header { background: #1e293b; padding: 10px; color: #38bdf8; font-size: 13px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; right: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 14px; label { display: block; font-size: 11px; color: #94a3b8; font-weight: 900; } .val { font-size: 15px; color: #f8fafc; font-weight: 900; font-family: monospace; } &.cyan { border-left: 4px solid #06b6d4; } } } }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 14px 18px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 12px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.discovery-card-compact { background: #f0f9ff; padding: 14px 18px; border-radius: 12px; border: 1px solid #bae6fd; display: flex; align-items: flex-start; gap: 12px; i { font-size: 1.2rem; margin-top: 2px; } .discovery-content { flex: 1; p { margin: 0; font-size: 13px; color: #0284c7; line-height: 1.6; strong { color: #075985; font-weight: 900; } } } }
</style>
